'use client';

import { useState } from 'react';
import { ChevronDown, ChevronUp, List } from 'lucide-react';
import { generateAnchorId } from '@/lib/utils/anchor';

interface TOCItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  /** The raw content to parse for headings */
  content: string;
  /** Optional custom title */
  title?: string;
}

/**
 * Parses content to extract H2 and H3 headings for table of contents
 */
function parseHeadings(content: string): TOCItem[] {
  const headings: TOCItem[] = [];
  const lines = content.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();

    // Match ## headings (H2)
    if (trimmed.startsWith('## ') && !trimmed.startsWith('### ')) {
      const text = trimmed.slice(3).trim();
      // Skip "Common Questions" and "Frequently Asked Questions" as they're at the end
      if (!text.toLowerCase().includes('common questions') &&
          !text.toLowerCase().includes('frequently asked')) {
        headings.push({
          id: generateAnchorId(text),
          text,
          level: 2,
        });
      }
    }
    // Match ### headings (H3) - only include if we want nested TOC
    // else if (trimmed.startsWith('### ')) {
    //   const text = trimmed.slice(4).trim();
    //   headings.push({
    //     id: generateAnchorId(text),
    //     text,
    //     level: 3,
    //   });
    // }
  }

  return headings;
}

/**
 * Handles smooth scrolling to an anchor with offset for fixed headers
 */
function scrollToHeading(id: string) {
  const element = document.getElementById(id);
  if (element) {
    const offset = 100; // Account for any fixed header
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
}

export default function TableOfContents({ content, title = 'In This Article' }: TableOfContentsProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const headings = parseHeadings(content);

  // Don't render if less than 3 headings
  if (headings.length < 3) {
    return null;
  }

  return (
    <nav
      className="my-8 bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-xl overflow-hidden"
      aria-label="Table of contents"
    >
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-teal-50/50 transition-colors"
        aria-expanded={isExpanded}
        aria-controls="toc-list"
      >
        <div className="flex items-center gap-2">
          <List className="h-5 w-5 text-teal-600" />
          <span className="font-semibold text-teal-800">{title}</span>
          <span className="text-sm text-teal-600">({headings.length} sections)</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-teal-600" />
        ) : (
          <ChevronDown className="h-5 w-5 text-teal-600" />
        )}
      </button>

      {/* TOC List - collapsible */}
      {isExpanded && (
        <ol
          id="toc-list"
          className="px-4 pb-4 space-y-1"
        >
          {headings.map((heading, index) => (
            <li key={heading.id}>
              <button
                onClick={() => scrollToHeading(heading.id)}
                className={`
                  w-full text-left py-1.5 px-3 rounded-lg text-gray-700 hover:bg-teal-100/50 hover:text-teal-800 transition-colors
                  ${heading.level === 3 ? 'ml-4 text-sm' : ''}
                `}
              >
                <span className="text-teal-600 font-medium mr-2">{index + 1}.</span>
                {heading.text}
              </button>
            </li>
          ))}
        </ol>
      )}
    </nav>
  );
}

/**
 * Adds anchor IDs to headings in parsed HTML content
 * This should be called during content parsing to add IDs to h2 elements
 */
export function addHeadingIds(html: string): string {
  // Add IDs to h2 tags for anchor navigation
  return html.replace(
    /<h2([^>]*)>([^<]+)<\/h2>/g,
    (match, attrs, text) => {
      const id = generateAnchorId(text);
      // Check if id already exists in attrs
      if (attrs.includes('id=')) {
        return match;
      }
      return `<h2${attrs} id="${id}">${text}</h2>`;
    }
  );
}

// Re-export generateAnchorId from the shared utility for backwards compatibility
export { generateAnchorId } from '@/lib/utils/anchor';
